import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";

// Date pickers
import { DatePicker } from "@mui/x-date-pickers";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import AttendeeSelector from "./AttendeeSelector"; // Importera den nya komponenten
import { useGetUsersCommunicationsQuery } from "../../store/services/communicationService";
import { 
  Attendee, 
  MeetingFormData, 
  MeetingDuration
} from "./types";
import { WorkItemTypeEnum } from "../../store/services/timelineApiRTK";
import { FreeSlotChips } from "../free-slots";

// Helper function to detect the current meeting duration
const detectMeetingDuration = (
  startTime: Date,
  endTime: Date
): MeetingDuration => {
  const durationInMins =
    (endTime.getTime() - startTime.getTime()) / (60 * 1000);

  // Check if duration matches a predefined option
  const standardDurations = [
    MeetingDuration.FIFTEEN_MIN,
    MeetingDuration.THIRTY_MIN,
    MeetingDuration.ONE_HOUR,
    MeetingDuration.NINETY_MIN,
    MeetingDuration.TWO_HOURS,
  ];

  return standardDurations.includes(durationInMins as MeetingDuration)
    ? (durationInMins as MeetingDuration)
    : MeetingDuration.CUSTOM;
};

interface MeetingBookingFormProps {
  formData: MeetingFormData;
  onFormChange: (newData: MeetingFormData) => void;
  error?: string | null;
  customerNo: number;
  customerName?: string;
  workItemType?: WorkItemTypeEnum;
}

const MeetingBookingForm: React.FC<MeetingBookingFormProps> = ({
  formData,
  onFormChange,
  customerNo,
  customerName,
  error,
  workItemType,
}) => {
  const [validationError, setValidationError] = useState<string | null>(null);
  const { data: communications = [], isLoading } =
    useGetUsersCommunicationsQuery(customerNo);

  // Initialize duration state from props
  const initialDuration = detectMeetingDuration(
    formData.startDateTime,
    formData.endDateTime
  );
  const [selectedDuration, setSelectedDuration] =
    useState<MeetingDuration>(initialDuration);
  const [customEndTime, setCustomEndTime] = useState<Date | null>(
    formData.endDateTime
  );

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target;
    if (name) {
      onFormChange({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleDurationSelect = (duration: MeetingDuration) => {
    setSelectedDuration(duration);

    if (duration !== MeetingDuration.CUSTOM) {
      const newEndTime = new Date(
        formData.startDateTime.getTime() + duration * 60 * 1000
      );
      onFormChange({
        ...formData,
        endDateTime: newEndTime,
      });
    }
  };

  const handleCustomEndTimeChange = (newTime: Date | null) => {
    if (newTime) {
      setCustomEndTime(newTime);

      // If the end time is before start time, adjust it
      if (newTime < formData.startDateTime) {
        const adjustedTime = new Date(newTime);
        if (adjustedTime.getDate() === formData.startDateTime.getDate()) {
          // Same day, add 24 hours
          adjustedTime.setTime(adjustedTime.getTime() + 24 * 60 * 60 * 1000);
        }

        setCustomEndTime(adjustedTime);
        onFormChange({
          ...formData,
          endDateTime: adjustedTime,
        });
      } else {
        onFormChange({
          ...formData,
          endDateTime: newTime,
        });
      }
    }
  };

  const handleStartTimeChange = (newTime: Date | null) => {
    if (!newTime) return;

    // Create a new date with current start date but updated time
    const updatedStart = new Date(formData.startDateTime);
    updatedStart.setHours(newTime.getHours());
    updatedStart.setMinutes(newTime.getMinutes());

    // If we have a standard duration selected, adjust end time accordingly
    let updatedEnd = formData.endDateTime;
    if (selectedDuration !== MeetingDuration.CUSTOM) {
      updatedEnd = new Date(
        updatedStart.getTime() + selectedDuration * 60 * 1000
      );
    } else {
      // For custom duration, maintain the same time difference
      const currentDiff =
        formData.endDateTime.getTime() - formData.startDateTime.getTime();
      updatedEnd = new Date(updatedStart.getTime() + currentDiff);
    }

    setCustomEndTime(updatedEnd);
    onFormChange({
      ...formData,
      startDateTime: updatedStart,
      endDateTime: updatedEnd,
    });
  };

  // Faktisk möteslängd — vid Anpassad används diffen mellan start och slut
  const effectiveDuration =
    selectedDuration === MeetingDuration.CUSTOM
      ? Math.max(
          15,
          (formData.endDateTime.getTime() - formData.startDateTime.getTime()) /
            60000
        )
      : selectedDuration;

  const handleSlotSelect = (slot: Date) => {
    const updatedEnd = new Date(slot.getTime() + effectiveDuration * 60 * 1000);
    setCustomEndTime(updatedEnd);
    onFormChange({
      ...formData,
      startDateTime: slot,
      endDateTime: updatedEnd,
    });
  };

  const formatDurationLabel = (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    if (minutes === 60) return "1 timme";
    return `${minutes / 60} timmar`;
  };

  const handleAttendeesChange = (newAttendees: Attendee[]) => {
    onFormChange({
      ...formData,
      attendees: newAttendees,
    });
  };

  // Check if this is a physical meeting based on workItemType
  const isPhysicalMeeting = workItemType === WorkItemTypeEnum.FollowUpPhysicalMeeting;

  return (
    <Box sx={{ mt: 1 }}>
      {(error || validationError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || validationError}
        </Alert>
      )}

      {customerName && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="primary">
            Boka {isPhysicalMeeting ? "fysiskt möte" : "Teams-möte"} med {customerName}
          </Typography>
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Mötesämne"
            name="subject"
            value={formData.subject}
            onChange={handleInputChange}
            required
            fullWidth
          />
        </Grid>

        {/* Location field for physical meetings */}
        {isPhysicalMeeting && (
          <Grid item xs={12}>
            <TextField
              label="Plats"
              name="location"
              value={formData.location || ""}
              onChange={handleInputChange}
              required
              fullWidth
              placeholder="T.ex. Konferensrum 1, Kundkontor, Café"
              helperText="Ange var mötet ska äga rum"
            />
          </Grid>
        )}

        {/* Startdatum med datepicker */}
        <Grid item xs={12} sm={6}>
          <DatePicker
            label="Datum"
            value={formData.startDateTime}
            onChange={(newDate) => {
              if (newDate) {
                const updatedStart = new Date(formData.startDateTime);
                updatedStart.setFullYear(newDate.getFullYear());
                updatedStart.setMonth(newDate.getMonth());
                updatedStart.setDate(newDate.getDate());

                // Update end date to be same day
                const updatedEnd = new Date(formData.endDateTime);
                updatedEnd.setFullYear(newDate.getFullYear());
                updatedEnd.setMonth(newDate.getMonth());
                updatedEnd.setDate(newDate.getDate());

                // Preserve duration
                if (selectedDuration !== MeetingDuration.CUSTOM) {
                  updatedEnd.setTime(
                    updatedStart.getTime() + selectedDuration * 60 * 1000
                  );
                }

                onFormChange({
                  ...formData,
                  startDateTime: updatedStart,
                  endDateTime: updatedEnd,
                });
              }
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
              },
            }}
          />
        </Grid>

        {/* Starttid med timepicker */}
        <Grid item xs={12} sm={6}>
          <TimePicker
            label="Starttid"
            value={formData.startDateTime}
            onChange={handleStartTimeChange}
            minutesStep={5}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
              },
            }}
          />
        </Grid>

        {/* Duration options */}
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Typography variant="subtitle2">Möteslängd</Typography>
          <ButtonGroup
            variant="outlined"
            aria-label="möteslängd"
            sx={{ mt: 1, flexWrap: "wrap" }}
          >
            {[
              MeetingDuration.FIFTEEN_MIN,
              MeetingDuration.THIRTY_MIN,
              MeetingDuration.ONE_HOUR,
              MeetingDuration.NINETY_MIN,
              MeetingDuration.TWO_HOURS,
            ].map((duration) => (
              <Button
                key={duration}
                onClick={() => handleDurationSelect(duration)}
                variant={
                  selectedDuration === duration ? "contained" : "outlined"
                }
                sx={{ mb: 1 }}
              >
                {formatDurationLabel(duration)}
              </Button>
            ))}
            <Button
              onClick={() => handleDurationSelect(MeetingDuration.CUSTOM)}
              variant={
                selectedDuration === MeetingDuration.CUSTOM
                  ? "contained"
                  : "outlined"
              }
              sx={{ mb: 1 }}
            >
              Anpassad
            </Button>
          </ButtonGroup>
        </Grid>

        {/* Lediga tider för valt datum och möteslängd */}
        <Grid item xs={12}>
          <FreeSlotChips
            date={formData.startDateTime}
            durationMinutes={effectiveDuration}
            selectedTime={formData.startDateTime}
            onSelectSlot={handleSlotSelect}
          />
        </Grid>

        {/* Custom end time picker - only visible when custom duration is selected */}
        {selectedDuration === MeetingDuration.CUSTOM && (
          <Grid item xs={12} sm={6}>
            <TimePicker
              label="Sluttid"
              value={customEndTime}
              onChange={handleCustomEndTimeChange}
              minutesStep={15}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                },
              }}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            label="Beskrivning"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            multiline
            rows={3}
            fullWidth
          />
        </Grid>
      </Grid>

      {/* Använd den nya AttendeeSelector-komponenten */}
      <AttendeeSelector
        attendees={formData.attendees}
        onAttendeesChange={handleAttendeesChange}
        communications={communications}
        isLoading={isLoading}
        validationError={validationError}
        onValidationErrorChange={setValidationError}
      />
    </Box>
  );
};

export default MeetingBookingForm;
